import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "reactstrap";
import { Helmet } from "react-helmet";
import Spinner from "../../Spinner/spinner";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { localeEs } from "../../locale/ag-grid-es";
import Swal from "sweetalert2";
import BreadCrum from "../../breadCrum/breadCrum";
import withTokenCheck from "../../withTokenCheck";

/* ------------------------------------------------------------------ */
/* Helpers de fecha                                                    */
/* ------------------------------------------------------------------ */

// Fecha local en YYYY-MM-DD. No usar toISOString() directo:
// en Colombia (UTC-5) te devuelve el día anterior.
const toLocalISODate = (date) => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

// Normaliza cualquier fecha que venga del backend a YYYY-MM-DD.
// Devuelve null si no la puede interpretar.
const normalizarFecha = (valor) => {
    if (!valor) return null;
    const s = String(valor).trim();

    // 2026-07-28 / 2026-07-28T00:00:00 / 2026-07-28 00:00:00
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;

    // 28-07-2026 / 28/07/2026
    m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : toLocalISODate(d);
};

// ISO -> DD/MM/YYYY, solo para mostrarle al usuario
const paraMostrar = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

/* ------------------------------------------------------------------ */
/* Helper de errores                                                   */
/* ------------------------------------------------------------------ */

// Con responseType: "blob", los errores de Laravel (422, 409, 500)
// llegan como Blob y error.response.data.message queda undefined.
const leerErrorBlob = async (error) => {
    const data = error.response?.data;
    if (data instanceof Blob) {
        try {
            return JSON.parse(await data.text());
        } catch {
            return null;
        }
    }
    return data;
};

const extraerMensajeError = (payload) => {
    if (!payload) return "Se produjo un error al cerrar el caso";
    if (payload.errors) {
        return Object.values(payload.errors).flat().join("\n");
    }
    return payload.message ?? "Se produjo un error al cerrar el caso";
};

/* ------------------------------------------------------------------ */
/* Componente                                                          */
/* ------------------------------------------------------------------ */

const DIAS_VENTANA = 14; // ventana permitida hacia atrás, incluyendo hoy

const AppCheckOut = ({ token }) => {
    const [info, setInfo] = useState([]);
    const [loading, setLoading] = useState(true);
    const gridApi = useRef(null);
    const etiquetas = [
        "Inicio Lossman",
        "Cambios De Evento",
        "Proceso De Check-Out",
    ];

    const cargarInfo = () => {
        if (!token) return;
        setLoading(true);
        axios
            .get("/formulario/consulta/checkout", {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setInfo(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No fue posible cargar los casos pendientes de check-out",
                    showCloseButton: true,
                });
            });
    };

    useEffect(() => {
        cargarInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleUpdateTable = () => cargarInfo();

    /* -------------------------------------------------------------- */
    /* Check-out                                                       */
    /* -------------------------------------------------------------- */

    const handleCheckOutClick = (element) => {
        if (!token) return;

        // --- Cálculo del rango permitido -----------------------------
        const hoy = new Date();
        const limiteAtras = new Date();
        limiteAtras.setDate(hoy.getDate() - (DIAS_VENTANA - 1));

        const maxFecha = toLocalISODate(hoy);
        let minFecha = toLocalISODate(limiteAtras);

        // La salida no puede ser anterior al ingreso del paciente
        const fechaIngreso = normalizarFecha(element.FechaInicial);
        if (fechaIngreso && fechaIngreso > minFecha) {
            minFecha = fechaIngreso;
        }

        // Red de seguridad: si min > max el input date se rompe en silencio
        if (minFecha > maxFecha) {
            minFecha = maxFecha;
        }

        const nombrePaciente = element.paciente
            ? [
                  element.paciente.Nombre1,
                  element.paciente.Nombre2,
                  element.paciente.Apellido1,
                  element.paciente.Apellido2,
              ]
                  .filter(Boolean)
                  .join(" ")
            : "";

        // --- Paso 1: confirmación ------------------------------------
        Swal.fire({
            icon: "info",
            title: "¿Está seguro de cerrar el caso?",
            text: nombrePaciente
                ? `Paciente: ${nombrePaciente} — Caso ${element.NumeroCaso ?? ""}`
                : undefined,
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, cerrar caso",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (!result.isConfirmed) return;

            // --- Paso 2: formulario unificado ------------------------
            Swal.fire({
                icon: "question",
                title: "Cierre del caso",
                width: 600,
                html: `
                  <div style="text-align:left;margin-bottom:1rem;">
                    <label for="fechaFinal" style="display:block;font-weight:600;margin-bottom:.35rem;">
                      Fecha final (fecha real de salida) <span style="color:#d33">*</span>
                    </label>
                    <input
                      type="date"
                      id="fechaFinal"
                      class="swal2-input"
                      style="width:100%;margin:0;display:block;"
                      min="${minFecha}"
                      max="${maxFecha}"
                      value="${maxFecha}"
                    />
                    <small style="display:block;margin-top:.35rem;color:#666;">
                      Solo se permiten fechas entre ${paraMostrar(minFecha)} y ${paraMostrar(maxFecha)}.
                    </small>
                  </div>

                  <div style="text-align:left;margin-bottom:1rem;">
                    <label style="display:block;font-weight:600;margin-bottom:.35rem;">
                      ¿Desea facturar el caso? <span style="color:#d33">*</span>
                    </label>
                    <div class="swal2-radio" style="display:flex;justify-content:flex-start;margin:0;">
                      <label style="margin-right:1.5rem;">
                        <input type="radio" name="opcion" value="facturar" />
                        <span>Facturar</span>
                      </label>
                      <label>
                        <input type="radio" name="opcion" value="nofacturar" />
                        <span>No Facturar</span>
                      </label>
                    </div>
                  </div>

                  <div style="text-align:left;">
                    <label for="observaciones" style="display:block;font-weight:600;margin-bottom:.35rem;">
                      Observaciones: <span id="obsRequerida" style="color:#d33;display:none;">*</span>
                    </label>
                    <textarea
                      id="observaciones"
                      class="swal2-textarea"
                      style="width:100%;margin:0;display:block;"
                      maxlength="1000"
                    ></textarea>
                  </div>
                `,
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Aceptar",
                cancelButtonText: "Cancelar",
                focusConfirm: false,
                didOpen: () => {
                    // Marca visualmente que las observaciones son obligatorias
                    // cuando se elige "No Facturar"
                    const popup = Swal.getPopup();
                    const marca = popup.querySelector("#obsRequerida");
                    popup
                        .querySelectorAll('input[name="opcion"]')
                        .forEach((radio) => {
                            radio.addEventListener("change", (e) => {
                                marca.style.display =
                                    e.target.value === "nofacturar"
                                        ? "inline"
                                        : "none";
                            });
                        });
                },
                preConfirm: () => {
                    const popup = Swal.getPopup();
                    const fechaFinal = popup.querySelector("#fechaFinal").value;
                    const opcion = popup.querySelector(
                        'input[name="opcion"]:checked'
                    )?.value;
                    const observaciones = popup
                        .querySelector("#observaciones")
                        .value.trim();

                    if (!fechaFinal) {
                        Swal.showValidationMessage(
                            "Debes indicar la fecha final"
                        );
                        return false;
                    }
                    if (fechaFinal < minFecha || fechaFinal > maxFecha) {
                        Swal.showValidationMessage(
                            `La fecha final debe estar entre ${paraMostrar(
                                minFecha
                            )} y ${paraMostrar(maxFecha)}`
                        );
                        return false;
                    }
                    if (!opcion) {
                        Swal.showValidationMessage(
                            "Debes seleccionar una opción de facturación"
                        );
                        return false;
                    }
                    if (opcion === "nofacturar" && !observaciones) {
                        Swal.showValidationMessage(
                            "Debes indicar el motivo por el cual no se debe facturar"
                        );
                        return false;
                    }

                    return { opcion, observaciones, fechaFinal };
                },
            }).then((resultForm) => {
                if (!resultForm.isConfirmed) return;

                const { opcion, observaciones, fechaFinal } = resultForm.value;

                // --- Paso 3: envío ----------------------------------
                Swal.fire({
                    title: "Procesando...",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => Swal.showLoading(),
                });

                axios
                    .post(
                        `/formulario/checkout/${element.id}`,
                        {
                            EstadoFacturacion: opcion === "facturar" ? 1 : 0,
                            ObservacionesFacturacion: observaciones,
                            FechaFinal: fechaFinal,
                        },
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: "blob",
                        }
                    )
                    .then((response) => {
                        Swal.fire({
                            icon: "success",
                            title: "Caso cerrado con éxito",
                            text: `Fecha final registrada: ${paraMostrar(
                                fechaFinal
                            )}. ¿Desea descargar el formato de checkout?`,
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Sí, descargar",
                            cancelButtonText: "No",
                        }).then((resultDescarga) => {
                            if (resultDescarga.isConfirmed) {
                                descargarPDF(response, element);
                            }
                        });

                        handleUpdateTable();
                    })
                    .catch(async (error) => {
                        console.error(error);
                        const payload = await leerErrorBlob(error);
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: extraerMensajeError(payload),
                            showCloseButton: true,
                        });
                    });
            });
        });
    };

    /* -------------------------------------------------------------- */
    /* Descarga del PDF                                                */
    /* -------------------------------------------------------------- */

    const descargarPDF = (response, element) => {
        // axios normaliza headers a minúsculas; el ?.get cubre fetch/AxiosHeaders
        const disposition =
            response.headers?.["content-disposition"] ??
            response.headers?.get?.("Content-Disposition");

        const match = disposition?.match(/filename="?([^";]+)"?/);
        const fileName = match
            ? match[1]
            : `checkout_${element.NumeroCaso ?? element.id}.pdf`;

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    /* -------------------------------------------------------------- */
    /* Grid                                                            */
    /* -------------------------------------------------------------- */

    const defaultColDef = {
        sortable: true,
        resizable: true,
        filter: true,
        editable: false, // el grid es de solo lectura; la edición va por el modal
    };

    const columnDefs = [
        {
            field: "id",
            headerName: "Check Out",
            filter: false,
            sortable: false,
            cellRenderer: (params) => (
                <Button
                    color="secondary"
                    onClick={() => handleCheckOutClick(params.data)}
                >
                    Check out
                </Button>
            ),
        },
        {
            headerName: "Paciente",
            field: "paciente",
            valueGetter: (params) =>
                [
                    params.data?.paciente?.Nombre1,
                    params.data?.paciente?.Nombre2,
                    params.data?.paciente?.Apellido1,
                    params.data?.paciente?.Apellido2,
                ]
                    .filter(Boolean)
                    .join(" "),
        },
        { headerName: "Numero De Caso", field: "NumeroCaso" },
        { headerName: "Hogar", field: "hogar.Nombre" },
        {
            headerName: "Fecha Inicial",
            field: "FechaInicial",
            valueFormatter: (params) =>
                paraMostrar(normalizarFecha(params.value)),
        },
        {
            headerName: "Fecha Final",
            field: "FechaFinal",
            valueFormatter: (params) =>
                paraMostrar(normalizarFecha(params.value)),
        },
        { headerName: "Usuario Que Actualizo", field: "updated_by.name" },
        { headerName: "Fecha Actualización", field: "updated_at" },
    ];

    const handleExportCSV = () => {
        gridApi.current?.exportDataAsCsv();
    };

    const onGridReady = (params) => {
        gridApi.current = params.api;
    };

    return (
        <>
            <Helmet>
                <title>Check Out</title>
            </Helmet>
            <div style={{ position: "relative" }}>
                {loading && <Spinner />}
                <div>
                    <BreadCrum etiquetas={etiquetas} />
                </div>
                <div style={{ opacity: loading ? 0.5 : 1 }}>
                    <div className="container-fluid mt-5">
                        <Button color="success" onClick={handleExportCSV}>
                            Exportar CSV (Excel)
                        </Button>
                        <div
                            className="mt-3"
                            style={{ height: "500px", width: "100%" }}
                        >
                            <div
                                className="ag-theme-alpine"
                                style={{ height: "100%", width: "100%" }}
                            >
                                <AgGridReact
                                    rowData={info}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    pagination={true}
                                    onGridReady={onGridReady}
                                    localeText={localeEs}
                                    paginationPageSize={20}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTokenCheck(AppCheckOut);